// src/components/league-settings/DeleteLeagueModal.js
//
// Extracted from inline renderDeleteModal() function in
// LeagueSettings.js per IMPORTANT batch 2 commit 12a (refactor only —
// no behavior change).
//
// Controlled-component pattern: state lives in the parent
// (showDeleteModal, deleteStep, deleteConfirmText, finalConfirmation);
// this modal receives values + setters via props. The 3-step gate
// logic (initial → typed-name match → checkbox) is byte-identical to
// the inline source.
import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

const DeleteLeagueModal = ({
  isOpen,
  step,
  setStep,
  confirmText,
  setConfirmText,
  finalChecked,
  setFinalChecked,
  leagueName,
  saving,
  onConfirmDelete,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${dynastyTheme.components.card.base} p-6 w-full max-w-md mx-4`}>
        {step === 1 && (
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className={`w-8 h-8 ${dynastyTheme.classes.text.warning}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Delete League</h3>
            </div>

            <div className="space-y-4">
              <p className={dynastyTheme.classes.text.white}>
                Are you sure you want to delete <strong>"{leagueName}"</strong>?
              </p>

              <div className={`p-4 rounded-lg bg-red-500/10 border border-red-500/20`}>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  This action is <strong className={dynastyTheme.classes.text.white}>permanent and irreversible</strong>.
                  All league data, team rosters, transaction history, and settings will be permanently deleted.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} flex-1`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  className={`${dynastyTheme.classes.bg.error} ${dynastyTheme.classes.text.white} flex-1 px-4 py-2 rounded-lg font-semibold ${dynastyTheme.classes.transition}`}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <Trash2 className={`w-8 h-8 ${dynastyTheme.classes.text.error}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Confirm Deletion</h3>
            </div>

            <div className="space-y-4">
              <p className={dynastyTheme.classes.text.white}>
                Type the league name <strong>"{leagueName}"</strong> to confirm deletion:
              </p>

              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className={`${dynastyTheme.components.input} w-full`}
                placeholder="Enter league name"
                autoFocus
              />

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep(1)}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} flex-1`}
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={confirmText !== leagueName}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold ${dynastyTheme.classes.transition} disabled:opacity-50 ${dynastyTheme.classes.text.white}`}
                  style={{
                    backgroundColor: confirmText === leagueName
                      ? dynastyTheme.tokens.colors.error
                      : dynastyTheme.tokens.colors.neutral[600]
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className={`w-8 h-8 ${dynastyTheme.classes.text.error}`} />
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Final Confirmation</h3>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg border-2 bg-red-500/10 border-red-500`}>
                <p className={`${dynastyTheme.classes.text.white} font-semibold mb-2`}>This will permanently delete:</p>
                <ul className={`text-sm space-y-1 ${dynastyTheme.classes.text.neutralLight}`}>
                  <li>• League configuration and settings</li>
                  <li>• All team rosters and player assignments</li>
                  <li>• Complete transaction history</li>
                  <li>• League standings and statistics</li>
                  <li>• All league-specific data</li>
                </ul>
              </div>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={finalChecked}
                  onChange={(e) => setFinalChecked(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className={`${dynastyTheme.classes.text.white} text-sm`}>
                  I understand this action cannot be undone
                </span>
              </label>

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep(2)}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} flex-1`}
                >
                  Back
                </button>
                <button
                  onClick={onConfirmDelete}
                  disabled={!finalChecked || saving}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold ${dynastyTheme.classes.transition} disabled:opacity-50 flex items-center justify-center space-x-2 ${dynastyTheme.classes.text.white}`}
                  style={{
                    backgroundColor: finalChecked
                      ? dynastyTheme.tokens.colors.error
                      : dynastyTheme.tokens.colors.neutral[600]
                  }}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete League Forever</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteLeagueModal;
